import { useCallback, useState } from 'react';
import {
  Alert,
  Button,
  Input,
  Modal,
  Select,
  Space,
  Steps,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import { matchHeaders, readWorkbook, resolveSheetName, sheetToHeadersRows } from '../../shared/import/workbook.ts';
import { PASSWORD_MIN_LENGTH } from '@lens/shared';
import { ApiError } from '../../shared/api/client.ts';
import { RemoteSourcePanel } from '../../shared/import/RemoteSourcePanel.tsx';
import { radius, spacing } from '../../shared/theme/tokens.ts';
import { createAdminUser } from './api.ts';

const { Text } = Typography;

/**
 * Импорт пользователей из CSV/XLSX. Зеркалит UX `DictCsvImportModal`:
 * шаги «загрузка → маппинг → результат», авто-маппинг по заголовкам,
 * импорт строк по одной через существующий `POST /api/admin/users`.
 *
 * Поля цели: login, displayName, email, password, isPlatformAdmin.
 * Если password в строке пуст — генерим случайный (10+ символов, не
 * совпадающий с логином, чтобы пройти `checkPasswordPolicy`). Сгенери-
 * рованные пароли возвращаются в результат, чтобы админ мог их раздать.
 */

type TargetField = 'login' | 'displayName' | 'email' | 'password' | 'isPlatformAdmin';

interface FieldDef {
  key: TargetField;
  label: string;
  required: boolean;
  aliases: string[];
}

const FIELDS: FieldDef[] = [
  {
    key: 'login',
    label: 'Логин',
    required: true,
    aliases: ['login', 'логин', 'username', 'user', 'пользователь'],
  },
  {
    key: 'displayName',
    label: 'Отображаемое имя',
    required: true,
    aliases: ['displayname', 'имя', 'фио', 'name', 'fullname', 'full name', 'отображаемое имя'],
  },
  {
    key: 'email',
    label: 'Email',
    required: false,
    aliases: ['email', 'почта', 'e-mail', 'mail'],
  },
  {
    key: 'password',
    label: 'Пароль',
    required: false,
    aliases: ['password', 'пароль', 'pass', 'pwd'],
  },
  {
    key: 'isPlatformAdmin',
    label: 'Администратор',
    required: false,
    aliases: ['isplatformadmin', 'admin', 'администратор', 'is_admin', 'isadmin', 'роль', 'role'],
  },
];

interface ColMapping {
  header: string;
  field: TargetField | null;
}

interface SuccessRow {
  row: number;
  login: string;
  password: string | null; // если password сгенерён — показываем; если пришёл из файла — null
}

interface ErrorRow {
  row: number;
  login: string;
  message: string;
}

interface ImportResult {
  created: SuccessRow[];
  errors: ErrorRow[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}

// ─── helpers ────────────────────────────────────────────────────────────

function autoMap(headers: string[]): ColMapping[] {
  const matched = matchHeaders(headers, FIELDS, (f) => [f.key, ...f.aliases]);
  return headers.map((header) => ({ header, field: matched.get(header)?.key ?? null }));
}

/**
 * Генерим пароль ≥ PASSWORD_MIN_LENGTH из читаемого алфавита (без 0/O/1/I/l).
 * Используем `crypto.getRandomValues` — это не «секретно секретный» секрет,
 * но намеренно не Math.random, чтобы коллег по offline-импорту не подставить.
 */
function generatePassword(length = Math.max(12, PASSWORD_MIN_LENGTH)): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%';
  const buf = new Uint32Array(length);
  crypto.getRandomValues(buf);
  let out = '';
  for (let i = 0; i < length; i++) out += alphabet[buf[i]! % alphabet.length];
  return out;
}

function parseBool(raw: string): boolean {
  return ['true', 'да', '1', 'yes', 'y', 'admin', 'администратор'].includes(
    raw.trim().toLowerCase(),
  );
}

/**
 * Парсим .csv / .xlsx / .xls единообразно через SheetJS. `sheetName` —
 * выбранная в UI вкладка (для книг с несколькими листами); если не
 * передана — берётся первый лист.
 */
async function parseFile(
  file: File,
  sheetName?: string,
): Promise<{ headers: string[]; rows: string[][] }> {
  const wb = await readWorkbook(file);
  const name = resolveSheetName(wb, sheetName);
  if (!name) return { headers: [], rows: [] };
  return sheetToHeadersRows(wb, name);
}

// ─── component ──────────────────────────────────────────────────────────

export function AdminUsersImportModal({ open, onClose, onDone }: Props): JSX.Element {
  const [step, setStep] = useState(0);
  const [fileName, setFileName] = useState('');
  const [headers, setHeaders] = useState<string[]>([]);
  const [allRows, setAllRows] = useState<string[][]>([]);
  const [previewRows, setPreviewRows] = useState<string[][]>([]);
  const [mapping, setMapping] = useState<ColMapping[]>([]);
  const [defaultIsAdmin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);

  const reset = useCallback(() => {
    setStep(0);
    setFileName('');
    setHeaders([]);
    setAllRows([]);
    setPreviewRows([]);
    setMapping([]);
    setResult(null);
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  const handleDone = useCallback(() => {
    handleClose();
    onDone();
  }, [handleClose, onDone]);

  const handleFile = useCallback((file: File, sheetName: string): void => {
    void parseFile(file, sheetName)
      .then(({ headers: h, rows }) => {
        if (h.length === 0 || rows.length === 0) {
          void message.warning('Файл пустой или содержит только заголовок');
          return;
        }
        setFileName(file.name);
        setHeaders(h);
        setAllRows(rows);
        setPreviewRows(rows.slice(0, 10));
        setMapping(autoMap(h));
        setStep(1);
      })
      .catch((err) => {
        void message.error(
          err instanceof Error ? `Не удалось прочитать файл: ${err.message}` : 'Не удалось прочитать файл',
        );
      });
  }, []);

  const fieldByKey = (key: TargetField | null): FieldDef | undefined =>
    key ? FIELDS.find((f) => f.key === key) : undefined;

  const usedFields = new Set(mapping.map((m) => m.field).filter((x): x is TargetField => !!x));
  const missingRequired = FIELDS.filter((f) => f.required && !usedFields.has(f.key));

  const handleImport = useCallback(async () => {
    setLoading(true);
    const res: ImportResult = { created: [], errors: [] };
    const colByField = new Map<TargetField, number>();
    for (const m of mapping) {
      if (m.field) colByField.set(m.field, headers.indexOf(m.header));
    }

    for (let i = 0; i < allRows.length; i++) {
      const row = allRows[i]!;
      const rowNum = i + 2; // +1 header, +1 1-based

      const get = (k: TargetField): string => {
        const idx = colByField.get(k);
        return idx == null || idx < 0 ? '' : (row[idx] ?? '').trim();
      };

      const login = get('login');
      const displayName = get('displayName');
      const emailRaw = get('email');
      const passwordRaw = get('password');
      const isAdminRaw = get('isPlatformAdmin');

      if (!login) {
        res.errors.push({ row: rowNum, login: '', message: 'Пустой логин' });
        continue;
      }
      if (!displayName) {
        res.errors.push({ row: rowNum, login, message: 'Пустое отображаемое имя' });
        continue;
      }

      let password = passwordRaw;
      let generated = false;
      if (!password) {
        // генерим, пока не получим не равный логину (политика на бэке)
        do {
          password = generatePassword();
        } while (password.toLowerCase() === login.toLowerCase());
        generated = true;
      }

      try {
        await createAdminUser({
          login,
          displayName,
          email: emailRaw || undefined,
          password,
          isPlatformAdmin: isAdminRaw ? parseBool(isAdminRaw) : defaultIsAdmin,
        });
        res.created.push({ row: rowNum, login, password: generated ? password : null });
      } catch (err) {
        const msg =
          err instanceof ApiError
            ? err.code === 'login_conflict'
              ? 'Логин уже существует'
              : err.code === 'password_policy_violation'
                ? `Пароль не соответствует политике: ${err.message}`
                : err.code === 'password_equals_login'
                  ? 'Пароль не должен совпадать с логином'
                  : err.message
            : err instanceof Error
              ? err.message
              : 'Ошибка';
        res.errors.push({ row: rowNum, login, message: msg });
      }
    }

    setResult(res);
    setStep(2);
    setLoading(false);
  }, [allRows, defaultIsAdmin, headers, mapping]);

  // ─── columns: mapping table ────────────────────────────────────────────

  const mappingCols = [
    {
      title: 'Колонка в файле',
      dataIndex: 'header',
      key: 'header',
      render: (v: string) => <Text strong>{v}</Text>,
    },
    {
      title: '',
      key: 'arrow',
      width: 40,
      render: () => <Text type="secondary">→</Text>,
    },
    {
      title: 'Поле пользователя',
      key: 'field',
      render: (_: unknown, record: ColMapping) => (
        <Select
          style={{ width: 220 }}
          value={record.field ?? '__skip__'}
          onChange={(val) =>
            setMapping((prev) =>
              prev.map((m) =>
                m.header === record.header
                  ? { ...m, field: val === '__skip__' ? null : (val as TargetField) }
                  : m,
              ),
            )
          }
        >
          <Select.Option value="__skip__">— не импортировать —</Select.Option>
          {FIELDS.map((f) => {
            const taken = mapping.some((m) => m.field === f.key && m.header !== record.header);
            return (
              <Select.Option key={f.key} value={f.key} disabled={taken}>
                {f.label}
                {f.required && ' *'}
              </Select.Option>
            );
          })}
        </Select>
      ),
    },
    {
      title: 'Пример',
      key: 'preview',
      render: (_: unknown, record: ColMapping) => {
        const idx = headers.indexOf(record.header);
        const val = previewRows[0]?.[idx] ?? '';
        return <Text type="secondary">{val || '—'}</Text>;
      },
    },
  ];

  const previewCols = [
    { title: '#', key: 'idx', width: 40, render: (_: unknown, __: unknown, i: number) => i + 1 },
    ...mapping
      .filter((m) => m.field)
      .map((m) => ({
        title: `${m.header} → ${fieldByKey(m.field)?.label ?? m.field}`,
        key: m.header,
        render: (_: unknown, row: string[]) => {
          const idx = headers.indexOf(m.header);
          const v = row[idx] ?? '';
          if (m.field === 'password' && v) return <Text type="secondary">••••••</Text>;
          return v || '—';
        },
      })),
  ];

  const generatedRows = (result?.created ?? []).filter((r) => r.password);

  const copyAll = (): void => {
    const text = generatedRows.map((r) => `${r.login}\t${r.password}`).join('\n');
    void navigator.clipboard.writeText(text).then(
      () => void message.success('Скопировано в буфер'),
      () => void message.error('Не удалось скопировать'),
    );
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      title="Импорт пользователей (CSV / Excel)"
      width={860}
      footer={null}
      destroyOnHidden
    >
      <Steps
        current={step}
        size="small"
        style={{ marginBottom: 24 }}
        items={[
          { title: 'Загрузка файла' },
          { title: 'Маппинг колонок' },
          { title: 'Результат' },
        ]}
      />

      {step === 0 && (
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          <RemoteSourcePanel
            accept=".csv,.tsv,.txt,.xlsx,.xls"
            formatsHint="Поддерживается .csv, .xlsx, .xls"
            onLoaded={handleFile}
          />
          <Alert
            type="info"
            showIcon
            message="Ожидаемые колонки"
            description={
              <span>
                <b>login</b>, <b>displayName</b>, <b>email</b>, <b>password</b>, <b>isPlatformAdmin</b>.
                <br />
                Обязательны только <b>login</b> и <b>displayName</b>. Если поле <b>password</b> в строке
                пустое — будет сгенерирован случайный пароль и показан в отчёте.
                <br />
                Заголовки могут быть на русском (<i>Логин, Имя, Email, Пароль, Администратор</i>) — авто-маппинг разберёт.
              </span>
            }
          />
        </Space>
      )}

      {step === 1 && (
        <Space direction="vertical" style={{ width: '100%' }} size={16}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text type="secondary">📄 {fileName} · {allRows.length} строк</Text>
            <Tag color={missingRequired.length === 0 ? 'green' : 'orange'}>
              {usedFields.size} из {FIELDS.length} полей сопоставлено
            </Tag>
          </div>

          {missingRequired.length > 0 && (
            <Alert
              type="warning"
              showIcon
              message={`Не сопоставлены обязательные поля: ${missingRequired.map((f) => f.label).join(', ')}`}
            />
          )}

          <Table
            dataSource={mapping}
            columns={mappingCols}
            rowKey="header"
            pagination={false}
            size="small"
            bordered
          />

          {previewRows.length > 0 && (
            <>
              <Text type="secondary" style={{ fontWeight: 500 }}>
                Превью (первые {previewRows.length} строк)
              </Text>
              <Table
                dataSource={previewRows}
                columns={previewCols}
                rowKey={(_, i) => String(i)}
                pagination={false}
                size="small"
                scroll={{ x: true, y: 240 }}
              />
            </>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={() => setStep(0)}>← Назад</Button>
            <Button
              type="primary"
              loading={loading}
              disabled={missingRequired.length > 0}
              onClick={() => void handleImport()}
            >
              Импортировать {allRows.length} пользователей →
            </Button>
          </div>
        </Space>
      )}

      {step === 2 && result && (
        <Space direction="vertical" style={{ width: '100%' }} size={16}>
          <div style={{ display: 'flex', gap: spacing.base }}>
            <div
              style={{
                flex: 1,
                textAlign: 'center',
                padding: spacing.base,
                background: 'var(--color-success-bg)',
                borderRadius: radius.md,
                border: `1px solid ${'var(--color-success)'}`,
              }}
            >
              <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--color-success)' }}>
                {result.created.length}
              </div>
              <div style={{ color: 'var(--color-text-secondary)', fontSize: 12, marginTop: spacing.xs }}>Создано</div>
            </div>
            <div
              style={{
                flex: 1,
                textAlign: 'center',
                padding: spacing.base,
                background: result.errors.length ? 'var(--color-error-bg)' : 'var(--color-bg-layout)',
                borderRadius: radius.md,
                border: `1px solid ${result.errors.length ? 'var(--color-error)' : 'var(--color-border)'}`,
              }}
            >
              <div
                style={{
                  fontSize: 28,
                  fontWeight: 700,
                  color: result.errors.length ? 'var(--color-error)' : 'var(--color-text-secondary)',
                }}
              >
                {result.errors.length}
              </div>
              <div style={{ color: 'var(--color-text-secondary)', fontSize: 12, marginTop: spacing.xs }}>Ошибок</div>
            </div>
          </div>

          {generatedRows.length > 0 && (
            <Space direction="vertical" style={{ width: '100%' }} size={8}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text strong>Сгенерированные пароли ({generatedRows.length})</Text>
                <Tooltip title="Скопировать как login\\tpassword (вставьте в Excel)">
                  <Button size="small" icon={<CopyOutlined />} onClick={copyAll}>
                    Копировать всё
                  </Button>
                </Tooltip>
              </div>
              <Alert
                type="warning"
                showIcon
                message="Пароли показаны один раз"
                description="После закрытия окна восстановить их будет нельзя — раздайте пользователям сейчас."
              />
              <Table
                size="small"
                pagination={{ pageSize: 10, hideOnSinglePage: true }}
                dataSource={generatedRows}
                rowKey="login"
                columns={[
                  { title: 'Логин', dataIndex: 'login', key: 'login' },
                  {
                    title: 'Пароль',
                    dataIndex: 'password',
                    key: 'password',
                    render: (v: string) => <Text code copyable>{v}</Text>,
                  },
                ]}
              />
            </Space>
          )}

          {result.errors.length > 0 && (
            <Space direction="vertical" style={{ width: '100%' }} size={8}>
              <Text strong>Ошибки</Text>
              <Table
                size="small"
                pagination={{ pageSize: 8, hideOnSinglePage: true }}
                dataSource={result.errors}
                rowKey={(r) => `${r.row}-${r.login}`}
                columns={[
                  { title: 'Строка', dataIndex: 'row', key: 'row', width: 90 },
                  { title: 'Логин', dataIndex: 'login', key: 'login', width: 200 },
                  { title: 'Ошибка', dataIndex: 'message', key: 'message' },
                ]}
              />
              <Input.TextArea
                value={result.errors.map((e) => `${e.row}\t${e.login}\t${e.message}`).join('\n')}
                rows={4}
                readOnly
                style={{ fontFamily: 'monospace', fontSize: 12 }}
              />
            </Space>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={reset}>Импортировать ещё</Button>
            <Button type="primary" onClick={handleDone}>
              К списку пользователей
            </Button>
          </div>
        </Space>
      )}
    </Modal>
  );
}
